/**
 * The dashboard time-frame filter.
 *
 * The chart API takes `from` / `to` as ISO calendar dates (YYYY-MM-DD). A preset
 * is stored as a day count so "Last 30 days" stays true tomorrow; a custom range
 * is stored as the two dates the viewer picked.
 */

export interface TimeframeConfig {
  enabled: boolean;
  /** Window applied until the viewer chooses another. */
  defaultDays: number;
}

export const DEFAULT_TIMEFRAME_DAYS = 30;

export const TIMEFRAME_PRESETS: Array<{ days: number; label: string }> = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 180, label: 'Last 6 months' },
  { days: 365, label: 'Last 12 months' },
];

/** Local calendar date as YYYY-MM-DD — not toISOString, which shifts by timezone. */
export const isoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** The `from` / `to` pair for "the last `days` days" — today and the `days - 1` before it. */
export const rangeForDays = (days: number, today = new Date()): { from: string; to: string } => {
  const from = new Date(today);
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDate(from), to: isoDate(today) };
};

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** A custom range is usable only when both ends are real dates and in order. */
export const validRange = (from: string, to: string): boolean =>
  ISO.test(from) && ISO.test(to) && from <= to;
